// Thin, shared wrapper around ReactQuill. Every surface migrating to the
// Word-ribbon standardization renders this instead of a bare <ReactQuill>,
// so format registration + the ribbon's supporting CSS are guaranteed to be
// set up exactly once, app-wide, instead of once per surface (the root
// cause of the pre-existing font/size inconsistency documented in
// wordFormats.js). Deliberately keeps the same imperative-ref shape
// (`ref.current.getEditor()`) every existing call site already uses, so
// migrating a surface is a drop-in swap of the `<ReactQuill .../>` tag, not
// a rewrite of the surrounding component.
import { forwardRef, useCallback, useEffect, useRef } from "react";
import ReactQuill from "react-quill";
import "react-quill/dist/quill.snow.css";
import { registerQuillFormats, WORD_RIBBON_CSS, insertHorizontalLine } from "./wordFormats";
import { cleanPastedHtml, escapeHtml, insertCleanedHtml, getDefaultPasteMode } from "./pasteClean";
import { usePasteOptionsWidget } from "./PasteOptions";

// react-quill only exposes the underlying Quill class as a static property
// on its default export (ReactQuill.Quill) — there is no named `Quill`
// export from the package itself (confirmed against node_modules/react-quill
// — same pattern OutlookWorkspace.js's own registerQuillFormats already
// relies on).
//
// Runs once, at module-evaluation time — before any RichTextEditor instance
// can mount and parse its initial `value` HTML into a Delta, so every
// document (new or previously-saved) is parsed with the final format set.
registerQuillFormats(ReactQuill.Quill);

let cssInjected = false;
function injectRibbonCssOnce() {
  if (cssInjected || typeof document === "undefined") return;
  cssInjected = true;
  const style = document.createElement("style");
  style.setAttribute("data-sdr-word-ribbon", "1");
  style.textContent = WORD_RIBBON_CSS;
  document.head.appendChild(style);
}

// No `toolbar` entry — WordRibbon.js drives formatting directly via
// quill.format()/formatLine(), so Quill's own auto-rendered toolbar bar
// (the redundant second toolbar previously visible in Mail Merge) is
// omitted entirely rather than built-then-hidden.
export const BASE_MODULES = {
  toolbar: false,
  history: { delay: 1000, maxStack: 200, userOnly: true },
};

export const BASE_FORMATS = [
  "font", "size", "bold", "italic", "underline", "underlinestyle", "strike",
  "script", "color", "background", "texteffect", "list", "bullet", "indent",
  "align", "header", "link", "image", "video", "blockquote", "code-block",
  "lineheight", "pshading", "pborder", "parabefore", "paraafter", "divider",
  "bulletstyle", "numstyle",
];

// Native Ctrl+V handler — cleans pasted HTML through pasteClean.js before
// it ever reaches Quill's own conversion, so a plain Ctrl+V from Word,
// Outlook, Google Docs, or a webpage behaves like Word/Outlook's own
// default paste (mode configurable via the "Paste Options" smart tag's
// "Set Default Paste..." — see PasteOptions.js — "Keep Source Formatting"
// cruft-stripped out of the box) instead of Quill's untouched stock
// behavior. Registered on `quill.container` (the `.ql-container` div, an
// ANCESTOR of the actual contenteditable `quill.root`) with
// `capture: true` — Quill's own clipboard module adds its paste listener
// directly on `quill.root` without capture, and DOM event order guarantees
// a capture-phase listener on an ancestor always runs before a same-event
// listener reached later on a descendant, so this fires first every time,
// letting us `preventDefault`+`stopPropagation` before Quill's own
// (uncleaned) handling ever runs. `WordRibbon.js`'s explicit "Paste
// Special" menu (no native paste event available for a button click) goes
// through the same `cleanPastedHtml`/`insertCleanedHtml` functions via the
// async Clipboard API instead — see its `doPasteSpecial`.
function makeNativePasteHandler(getQuill, onPasted) {
  return function handleNativePaste(e) {
    const quill = getQuill();
    if (!quill) return;
    const cd = e.clipboardData || window.clipboardData;
    if (!cd) return; // nothing we can intercept — let native/Quill default handling proceed
    const html = cd.getData("text/html");
    const text = cd.getData("text/plain");
    if (!html && !text) return; // e.g. a pasted file with no text/html representation — let Quill's default handle it
    e.preventDefault();
    e.stopPropagation();
    // Belt-and-suspenders: cleanPastedHtml/insertCleanedHtml already
    // catch their own internal failures and fall back gracefully, but
    // real-world Word/Outlook/Docs clipboard payloads are the messiest
    // input this app ever parses — since native/Quill-default paste has
    // already been prevented above, ANY uncaught exception here would
    // otherwise mean the paste silently does nothing at all. Falling all
    // the way back to the raw plain-text clipboard data guarantees a
    // paste always inserts *something* rather than appearing to fail.
    try {
      const mode = getDefaultPasteMode();
      const cleaned = (mode === "text" || !html) ? escapeHtml(text || "").replace(/\n/g, "<br>") : cleanPastedHtml(html, mode);
      const insertedRange = insertCleanedHtml(quill, cleaned);
      onPasted && onPasted(html || null, insertedRange);
    } catch (err) {
      console.error("RichTextEditor paste handler failed, falling back to raw plain text", err);
      try {
        const insertedRange = insertCleanedHtml(quill, escapeHtml(text || "").replace(/\n/g, "<br>"));
        onPasted && onPasted(null, insertedRange);
      } catch (err2) {
        console.error("RichTextEditor paste handler: plain-text fallback also failed", err2);
      }
    }
  };
}

// Word/Outlook's AutoFormat-As-You-Type "Horizontal Line" shorthand: typing
// three (or more) hyphens on an otherwise-empty line and pressing Enter
// converts that line into a horizontal-rule divider (see wordFormats.js's
// insertHorizontalLine — the exact same helper WordRibbon.js's manual
// "Horizontal Line" ribbon button uses, so both paths always produce an
// identical result). Registered on `quill.container` (an ANCESTOR of the
// actual contenteditable `quill.root`, where Quill's own keyboard module
// listens) with `capture: true` — the same technique the native paste
// handler above already relies on to reliably run, and win, before Quill's
// own Enter handling, regardless of whether Quill's own keyboard module
// happens to preventDefault for this key internally. Exported (not just
// used internally) so ComposeWindow.js's standalone popup — which renders
// its own bare <ReactQuill> instead of this wrapper — can wire up the exact
// same shorthand rather than re-implementing it.
export function makeAutoDividerHandler(getQuill) {
  return function handleKeydown(e) {
    if (e.key !== "Enter") return;
    const quill = getQuill();
    if (!quill) return;
    try {
      const sel = quill.getSelection();
      if (!sel) return;
      const text = quill.getText();
      const lineStart = text.lastIndexOf("\n", sel.index - 1) + 1;
      const line = text.slice(lineStart, sel.index).trim();
      if (line !== "---" && line !== "___") return; // not the divider shorthand — let Enter behave normally
      e.preventDefault();
      e.stopPropagation();
      quill.deleteText(lineStart, sel.index - lineStart, "user");
      insertHorizontalLine(quill, lineStart);
    } catch (err) {
      console.error("RichTextEditor auto-divider handler failed", err);
    }
  };
}

const RichTextEditor = forwardRef(function RichTextEditor(
  { modules, formats, formatMarksOn, className, ...rest }, ref
) {
  const innerRef = useRef(null);
  const setRef = useCallback((node) => {
    innerRef.current = node;
    if (typeof ref === "function") ref(node);
    else if (ref) ref.current = node;
  }, [ref]);

  useEffect(() => { injectRibbonCssOnce(); }, []);

  const getQuill = useCallback(() => innerRef.current && innerRef.current.getEditor && innerRef.current.getEditor(), []);
  const { show: showPasteOptions, node: pasteOptionsNode } = usePasteOptionsWidget(getQuill);

  useEffect(() => {
    const quill = getQuill();
    if (!quill || !quill.container) return;
    const handler = makeNativePasteHandler(getQuill, showPasteOptions);
    quill.container.addEventListener("paste", handler, true);
    return () => quill.container.removeEventListener("paste", handler, true);
  }, [getQuill, showPasteOptions]);

  useEffect(() => {
    const quill = getQuill();
    if (!quill || !quill.container) return;
    const handler = makeAutoDividerHandler(getQuill);
    quill.container.addEventListener("keydown", handler, true);
    return () => quill.container.removeEventListener("keydown", handler, true);
  }, [getQuill]);

  return (
    <>
      <ReactQuill
        ref={setRef}
        theme="snow"
        modules={modules || BASE_MODULES}
        formats={formats || BASE_FORMATS}
        className={(className || "") + (formatMarksOn ? " sdr-show-marks" : "")}
        {...rest}
      />
      {pasteOptionsNode}
    </>
  );
});

export default RichTextEditor;
